import { CommonError, FindAllParams, PageableResult } from "lego-gui-lib";
import {
  RemoteChildDataSource,
  RemoteChildDataSourceOptions
} from "lego-gui-lib/lib/dataAccess/RemoteChildDataSource";
import { RemoteListChildDataSource } from "lego-gui-lib/lib/dataAccess/RemoteListChildDataSource";
import { RemoteOneChildDataSource } from "lego-gui-lib/lib/dataAccess/RemoteOneChildDataSource";
import { action, observable, runInAction } from "mobx";
import { ReactText } from "react";
import { api, mainStore } from "../../../core/mainStore";
import { ConnectionDataSource } from "../connection/ConnectionDataSource";
import { BlockObject } from "./treeBlockOutputsDataSource";
import { ConnectionDef } from "../connection/schema";

export interface TreeStructureDef {
  blocks: BlockObject[];
  connections: ConnectionDef[];
  meta: {
    infos: string[];
    warnings: string[];
  };
}

export class TreeBlockDataSource<T, ID> extends RemoteChildDataSource<T, ID> {
  @observable public oneDataSource: TreeBlockOneDataSource<T, ID>;
  @observable public listDataSource: TreeBlockListDataSource<T, ID>;
  @observable public connectionDataSource: ConnectionDataSource<any, any>;

  @observable public treeStructure: TreeStructureDef = {
    blocks: [],
    connections: [],
    meta: { infos: [], warnings: [] }
  };

  public constructor(options: RemoteChildDataSourceOptions) {
    super(options);
    this.listDataSource = new TreeBlockListDataSource<T, ID>(options);
    this.oneDataSource = new TreeBlockOneDataSource<T, ID>({ ...options });
    this.connectionDataSource = mainStore.connection;
  }

  @action
  public async updateTreeBlock(
    values: any,
    treeBlockId: ReactText
  ): Promise<void> {
    try {
      await api.update(`${this.oneDataSource.url}/${treeBlockId}`, values);
      this.commons.newSuccess("Updated");
    } catch (e) {
      this.commons.newError(e.response.data as CommonError);
    }
  }

  @action
  public async getTreeStructure(): Promise<TreeStructureDef | false> {
    try {
      await this.oneDataSource.lock.waitForUnlock();
      const response = await this.api.getOne<TreeStructureDef>(
        `${this.oneDataSource.url}`,
        {}
      );
      runInAction(() => {
        this.treeStructure = response;
        this.connectionDataSource.listDataSource.list = this.treeStructure.connections;
      });
      return this.treeStructure;
    } catch (e) {
      this.commons.newError(e.response.data as CommonError);
    }
    return false;
  }

  @action
  public resetTreeStructure(): void {
    this.treeStructure = {
      blocks: [],
      connections: [],
      meta: { infos: [], warnings: [] }
    };
  }
}

export class TreeBlockOneDataSource<T, ID> extends RemoteOneChildDataSource<
  T,
  ID
> {
  @action
  public async changeTreeRootBlock(id: ID): Promise<void> {
    try {
      await this.lock.waitForUnlock();
      await this.api.update(`${this.url}/root/${id}`, {});
    } catch (e) {
      this.commons.newError(e.response.data as CommonError);
    }
    return;
  }
}

export class TreeBlockListDataSource<T, ID> extends RemoteListChildDataSource<
  T,
  ID
> {
  @action
  public async findAll(
    params: FindAllParams,
    force = false
  ): Promise<PageableResult<T>> {
    await this.lock.waitForUnlock();
    const parentIdString = JSON.stringify(this.parent.id);

    this.log.debug(`findAll with parent: ${parentIdString}`);
    return await this.executeRemote("list.load", async () => {
      const response = await this.api.getMethod<T>(this.url, {
        params: { filters: [] }
      });
      // @ts-ignore
      return this.findAllSuccess(response);
    });
  }

  @action
  public findAllSuccess(response: PageableResult<T>): PageableResult<T> {
    this.selected = [];
    // @ts-ignore
    this.treeStructure = response;
    this.total = response.totalElements;
    //@ts-ignore
    this.lastSuccess = Date.now();

    return response;
  }
}
